import * as d3 from 'd3';
import { Injectable } from '@angular/core';
import { SvgScriptLoaderService } from '../../services/SVG/svg-script-loader.service';

@Injectable()
export class AdjustPath {
  svg: any;
  line: any;
  svg1: HTMLElement;
  line1: any;
  isDragging = false;
  dragStartX: any;
  dragStartY: any;
  points: any;
  SVG: any;
  private svgScriptUrls = [
    'assets/js/svg/svg.min.js',
    'assets/js/svg/svg.resize.min.js',
    'assets/js/svg/svg.select.min.js',
  ];

  constructor(
    public originalPoints: any[],
    private scriptLoader: SvgScriptLoaderService,
  ) {
    this.drag = this.drag.bind(this);
    this.startDrag = this.startDrag.bind(this);
    this.endDrag = this.endDrag.bind(this);
    this.initializePath();
  }

  calculateAngle(cx: number, cy: number, px: number, py: number): number {
    return Math.atan2(py - cy, px - cx);
  }

  // Function to draw a line based on points
  drawLine(points) {
    const centroid: [number, number] = points
      .reduce(
        (acc, point) => {
          return [acc[0] + point.point_x, acc[1] + point.point_y];
        },
        [0, 0],
      )
      .map((coord) => coord / points.length) as [number, number];

    const sortedPoints: any[] = points.sort((a, b) => {
      const angleA = this.calculateAngle(centroid[0], centroid[1], a.point_x, a.point_y);
      const angleB = this.calculateAngle(centroid[0], centroid[1], b.point_x, b.point_y);
      return angleA - angleB;
    });

    let path = 'M' + sortedPoints[0].point_x + ' ' + sortedPoints[0].point_y;
    for (let i = 1; i < sortedPoints.length; i++) {
      path += ' L' + sortedPoints[i].point_x + ' ' + sortedPoints[i].point_y;
    }
    return path;
  }

  async initializePath() {
    this.points = [...this.originalPoints];
    if (document.getElementById('my-svg')) {
      await this.loadSVGJs(); //loads svg scripts dynamically because of the conflict with apexcharts
      this.svg = this.SVG('my-svg').size('100%', '100%');
      this.createPath();
    }

    // Get SVG container
    const svgContainer = document.getElementById('points-div');

    this.svg1 = document.getElementById('my-svg');

    // this.svg1.addEventListener('click', this.sendUpdatedPoints.bind(this));
    // this.svg1.addEventListener('mousedown', this.startDrag.bind(this));
    this.svg1.addEventListener('mousemove', this.drag.bind(this));
    this.svg1.addEventListener('mouseup', this.endDrag.bind(this));

    // Plot points on SVG container
    this.plotPoints(svgContainer, this.points);
  }

  async loadSVGJs() {
    try {
      for (const scriptUrl of this.svgScriptUrls) {
        await this.scriptLoader.loadScript(scriptUrl);
      }
      // Use dynamic import to load the SVG.js library and its plugins
      const SVGModule = await import('assets/js/svg/svg.min.js');
      this.SVG = SVGModule.default;

      // Import the plugins
      await import('assets/js/svg/svg.resize.min.js');
      await import('assets/js/svg/svg.select.min.js');
    } catch (error) {
      console.error('Error loading SVG.js scripts:', error);
    }
  }

  cleanupSVGJs() {
    for (const scriptUrl of this.svgScriptUrls) {
      this.scriptLoader.removeScript(scriptUrl);
    }
  }

  // Remember to remove the event listeners when done
  destroy() {
    if (this.svg1) {
      this.svg1.removeEventListener('click', this.sendUpdatedPoints.bind(this));
      this.svg1.removeEventListener('mousedown', this.startDrag.bind(this));
      this.svg1.removeEventListener('mousemove', this.drag.bind(this));
      this.svg1.removeEventListener('mouseup', this.endDrag.bind(this));
    }
    this.cleanupSVGJs();
  }

  startDrag(event) {
    if (event.target.tagName === 'path' || event.target.closest('g') === this.line.node) {
      this.isDragging = true;
      this.dragStartX = event.clientX;
      this.dragStartY = event.clientY;
    }
  }

  drag(event) {
    if (!this.isDragging) return;
    const deltaX = event.clientX - this.dragStartX;
    const deltaY = event.clientY - this.dragStartY;

    this.points.forEach((point) => {
      point.point_x = parseFloat(point.point_x) + deltaX;
      point.point_y = parseFloat(point.point_y) + deltaY;
    });

    this.updatePloting();

    this.line.remove();
    d3.selectAll('g').remove();

    // Recreate the line with updated points
    this.createPath();

    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
  }

  endDrag() {
    this.isDragging = false;
  }

  createPath() {
    this.line = this.svg
      .path(this.drawLine(this.points))
      .stroke({ color: 'transparent', width: 20 })
      .fill('transparent');

    this.line1 = document.getElementById(this.line.node.id);
    // this.line1.style.visibility = 'hidden';

    // Initialize resize and rotate functionality
    let resizer = this.line.selectize().resize({ saveAspectRatio: true }).rotate();
    this.removeSideBlocks();
    let that = this;
    resizer.on('resizing', function () {
      that.sendUpdatedPoints();
    });

    // Event listener for rotated event
    resizer.on('rotating', function () {
      that.sendUpdatedPoints();
    });

    this.line.on('mousedown', this.startDrag.bind(this));
    // this.line.on('mousemove', this.drag.bind(this));
    this.line.on('mouseup', this.endDrag.bind(this));
  }

  sendUpdatedPoints() {
    let updatedPoints = this.line.array().valueOf();
    const transformMatrix = this.line.transform();
    const parsedPoints = updatedPoints.map((point) => ({
      x: point[1],
      y: point[2],
    }));
    let transformPoints = this.calculateTransformedPoints(parsedPoints, transformMatrix);

    this.points.forEach((element, i) => {
      this.points[i].point_x = transformPoints[i].x;
      this.points[i].point_y = transformPoints[i].y;
    });

    this.updatePloting();
  }

  plotPoints(container, points) {
    points.forEach((point, index) => {
      const pointDiv = document.createElement('div');
      pointDiv.classList.add('point');
      pointDiv.id = 'point' + index;
      pointDiv.style.left = point.point_x + 'px';
      pointDiv.style.top = point.point_y + 'px';
      pointDiv.style.position = 'absolute';
      pointDiv.style.width = '10px';
      pointDiv.style.height = '10px';
      pointDiv.style.border = '1px solid black';
      pointDiv.style.borderRadius = '50%';
      pointDiv.style.background = 'yellow';
      container.appendChild(pointDiv);
    });
  }

  updatePloting() {
    this.points.forEach((point, index) => {
      const pointDiv = document.getElementById('point' + index);
      if (pointDiv) {
        pointDiv.style.left = point.point_x + 'px';
        pointDiv.style.top = point.point_y + 'px';
      }
    });
  }

  calculateTransformedPoints(points: any, transformMatrix: any) {
    const transformedPoints = [];
    points.forEach((point) => {
      const newX = transformMatrix.a * point.x + transformMatrix.c * point.y + transformMatrix.e;
      const newY = transformMatrix.b * point.x + transformMatrix.d * point.y + transformMatrix.f;
      transformedPoints.push({ x: newX, y: newY });
    });
    return transformedPoints;
  }

  removeSideBlocks() {
    let resizePoints = [
      'svg_select_points_t',
      'svg_select_points_l',
      'svg_select_points_b',
      'svg_select_points_r',
    ];
    resizePoints.forEach((p) => {
      d3.selectAll('circle.' + p).remove();
    });
    let circles: any = document.getElementsByClassName('svg_select_points');
    for (let i = 0; i < circles.length; i++) {
      circles[i].style.r = '5.5';
      circles[i].style.fill = 'blue';
    }
    let rotateCircle: any = document.getElementsByClassName('svg_select_points_rot');
    rotateCircle[0].style.fill = 'none';
    rotateCircle[0].style.r = '6.5';
    rotateCircle[0].style.strokeWidth = '2';
    rotateCircle[0].style.cursor = 'move';
  }

  getPoints() {
    return this.points;
  }

  getOriginalPoints() {
    return this.originalPoints;
  }

  reInitialize() {
    this.line.remove();
    d3.selectAll('g').remove();
    this.points = [...this.originalPoints];
    this.createPath();
    this.updatePloting();
  }
}
